import { Box, Button, Chip, IconButton, Snackbar, TextField, Typography, useTheme } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { tokens } from '../../theme';
import Header from '../../components/Header';
import ConfirmDeleteDialog from '../../components/ConfirmDeleteDialog';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';

const Reports = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastmessage] = useState('');
  const [allReports, setAllReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortModel, setSortModel] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);

  const token = localStorage.getItem('token');

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const getData = async () => {
    setLoading(true);

    // Prepare query parameters based on state
    const sortField = sortModel[0]?.field || 'e.ExcursionID';
    const sortOrder = sortModel[0]?.sort || 'desc';

    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(pageSize));
    params.set('sortBy', sortField);
    params.set('sortOrder', sortOrder.toUpperCase());
    if (search) params.set('search', search);
    if (departureDate) params.set('departureDate', departureDate);

    const res = await fetch(`${process.env.REACT_APP_APILINK}/api/admin/reports/excursions?${params.toString()}`, {
      headers: { token },
    });
    const data = await res.json();

    if (data.success) {
      setAllReports(data.data);
      setTotalCount(data?.pagination?.total || data.data.length);
    }
    setLoading(false);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setDepartureDate('');
    setPage(1);
  };

  // Function to convert backend date to 'YYYY-MM-DD' format
  const convertDate = (backendDate) => {
    const dateObject = new Date(backendDate);
    const day = String(dateObject.getDate()).padStart(2, '0');
    const month = String(dateObject.getMonth() + 1).padStart(2, '0');
    const year = dateObject.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const sortModelKey = useMemo(() => JSON.stringify(sortModel), [sortModel]);

  useEffect(() => {
    getData();
    // eslint-disable-next-line
  }, [page, pageSize, sortModelKey, search, departureDate]);

  // Delete Report
  const handleDelete = async (id) => {
    const res = await fetch(`${process.env.REACT_APP_APILINK}/api/admin/reports/excursions/${id}`, {
      method: 'DELETE',
      headers: { token },
    });
    const data = await res.json();
    if (data.message) {
      setOpenToast(true);
      setToastmessage(data.message);
      if (data.success) {
        getData();
      }
    }
  };

  const openDeleteDialog = (id) => {
    setIdToDelete(id);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setIdToDelete(null);
  };

  const confirmDelete = async () => {
    if (idToDelete == null) return;
    await handleDelete(idToDelete);
    closeDeleteDialog();
  };

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      hide: true,
      filterable: false,
      sortable: false,
    },
    {
      field: 'excursionName',
      headerName: 'Excursion Name',
      flex: 1,
      minWidth: 150,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'departureName',
      headerName: 'Destination Name',
      flex: 1,
      minWidth: 120,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'departDate',
      headerName: 'Departure Date',
      type: 'date',
      flex: 1,
      minWidth: 100,
      renderCell: (params) => <div>{params.row.departDate ? convertDate(params.row.departDate) : '-'}</div>,
    },
    {
      field: 'noOfPassengers',
      headerName: 'No. of Passengers',
      flex: 1,
      minWidth: 120,
      filterable: false,
      renderCell: (params) => <div>{params.row.noOfPassengers}</div>,
    },
    {
      field: 'View',
      filterable: false,
      sortable: false,
      renderCell: (cellValues) => (
        <Link to={`/reports/view/${cellValues.row.excursionId}`}>
          <Button
            sx={{
              backgroundColor: colors.blueAccent[600],
              color: colors.grey[100],
              ':hover': { backgroundColor: colors.blueAccent[700] },
            }}
            variant="contained"
          >
            <VisibilityIcon />
          </Button>
        </Link>
      ),
    },
    {
      field: 'Delete',
      filterable: false,
      sortable: false,
      renderCell: (cellValues) => (
        <Button
          sx={{
            backgroundColor: colors.redAccent[500],
            color: colors.grey[100],
            ':hover': { backgroundColor: colors.redAccent[600] },
          }}
          variant="contained"
          onClick={() => openDeleteDialog(cellValues.row.excursionId)}
        >
          <DeleteIcon />
        </Button>
      ),
    },
  ];

  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenToast(false);
  };

  const action = (
    <>
      <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseToast}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    setPage(1);
  };

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" gap="10px" flexWrap="wrap">
        <Header title="REPORTS" subtitle="All Excursion Reports" />
      </Box>

      <Box sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search by excursion name, destination..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: colors.grey[300] }} />,
          }}
          sx={{ minWidth: '320px', backgroundColor: colors.primary[400] }}
        />
        <TextField
          type="date"
          size="small"
          label="Departure Date"
          value={departureDate}
          onChange={(e) => {
            setDepartureDate(e.target.value);
            setPage(1);
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 200, backgroundColor: colors.primary[400] }}
        />
      </Box>

      {(search || departureDate) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          <Typography variant="body2" sx={{ color: colors.grey[300], fontWeight: 500 }}>
            Active filters:
          </Typography>
          {search && (
            <Chip
              label={`Search: ${search}`}
              size="small"
              onDelete={() => {
                setSearchInput('');
                setSearch('');
                setPage(1);
              }}
              sx={{ backgroundColor: colors.blueAccent[700], color: colors.grey[100], '& .MuiChip-deleteIcon': { color: colors.grey[300] } }}
            />
          )}
          {departureDate && (
            <Chip
              label={`Departure Date: ${departureDate}`}
              size="small"
              onDelete={() => {
                setDepartureDate('');
                setPage(1);
              }}
              sx={{ backgroundColor: colors.blueAccent[700], color: colors.grey[100], '& .MuiChip-deleteIcon': { color: colors.grey[300] } }}
            />
          )}
          {!loading && (
            <Typography variant="body2" sx={{ color: colors.greenAccent[400], ml: 1 }}>
              {totalCount} result{totalCount !== 1 ? 's' : ''}
            </Typography>
          )}
          <Typography
            variant="body2"
            onClick={handleClearSearch}
            sx={{
              color: colors.redAccent[500],
              cursor: 'pointer',
              ml: 1,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Clear all
          </Typography>
        </Box>
      )}

      <Box
        m="10px 0 0 0"
        height="667px"
        sx={{
          '& .MuiDataGrid-root': { border: 'none' },
          '& .MuiDataGrid-cell': { borderBottom: 'none' },
          '& .name-column--cell': { color: colors.greenAccent[300] },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: colors.blueAccent[700],
            borderBottom: 'none',
          },
          '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.primary[400] },
          '& .MuiDataGrid-footerContainer': {
            borderTop: 'none',
            backgroundColor: colors.blueAccent[700],
          },
          '& .MuiCheckbox-root': { color: `${colors.greenAccent[200]} !important` },
          '& .MuiDataGrid-toolbarContainer .MuiButton-text': {
            color: `${colors.grey[100]} !important`,
            fontSize: 14,
          },
        }}
      >
        <DataGrid
          rows={allReports}
          columns={columns}
          getRowId={(row) => row.id}
          disableColumnFilter
          pageSize={pageSize}
          rowsPerPageOptions={[10, 25, 50, 100]}
          pagination
          page={page - 1}
          sortingOrder={['asc', 'desc']}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          onPageChange={(newPage) => setPage(newPage + 1)} // DataGrid uses 0-based index for pages
          onPageSizeChange={(newPageSize) => { setPageSize(newPageSize); setPage(1); }}
          paginationMode="server" // Enable server-side pagination
          rowCount={totalCount} // Total number of rows (data.pagination.total)
          loading={loading}
        />
      </Box>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        title="Delete report?"
        message="Are you sure you want to delete this excursion report?"
        colors={colors}
      />

      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        message={toastMessage}
        action={action}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default Reports;
